// redux/actions/projectActions.ts
import { createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../axios'; // Use the configured axios instance with auth
import { Pagination, Project } from '../types/projectTypes';
import { uploadFileToS3 } from '../../utils/s3Upload';
import { clearCache } from '../slices/projectSlice';
import { RootState } from '../store';

export const fetchProjects = createAsyncThunk(
  'projects/fetchProjects',
  async (page: number, { rejectWithValue }) => {
    try {
      console.log(`Fetching projects for page ${page}`);
      const response = await axiosInstance.get<{
        success: boolean;
        data: Project[];
        pagination: Pagination;
      }>(`/v1/admin/project?page=${page}`); // Update to use the actual API endpoint from your backend
      console.log('fetchProjects response:', response.data);

      // Ensure pagination data is complete and correct
      if (response.data.pagination) {
        const pagination = response.data.pagination;
        // Calculate pages if not provided
        if (!pagination.pages && pagination.total && pagination.limit) {
          pagination.pages = Math.max(1, Math.ceil(pagination.total / pagination.limit));
          console.log(
            `Calculated total pages: ${pagination.pages} from ${pagination.total} items and limit ${pagination.limit}`
          );
        }
        // Set totalPages property for backward compatibility
        (pagination as any).totalPages = pagination.pages;
      }

      return response.data;
    } catch (error: any) {
      console.error('fetchProjects error:', error);
      return rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
);

// Define the possible return types for loadProjects
interface ProjectResponseWithCache {
  success: boolean;
  data: Project[];
  fromCache?: boolean;
  lastLoadTime?: number;
  pagination?: Pagination;
}

// New action to load all projects with caching and pagination
export const loadProjects = createAsyncThunk<
  ProjectResponseWithCache,
  | {
      page?: number;
      limit?: number;
      search?: string;
      customerName?: string;
      articleNo?: string;
      createdFrom?: string;
      createdTo?: string;
    }
  | undefined,
  { rejectValue: any; state: RootState }
>(
  'projects/loadProjects',
  async (params = { page: 1, limit: 10 }, { rejectWithValue, getState }) => {
    // Extract pagination parameters with defaults
    const {
      page = 1,
      limit = 10,
      search = '',
      customerName = '',
      articleNo = '',
      createdFrom = '',
      createdTo = '',
    } = params;
    try {
      // Check if we have cached data in Redux already
      const state = getState();
      const projects = state.projects.projects;
      const lastLoadTime = state.projects.lastLoadTime || 0;
      const now = Date.now();
      const cacheTime = 2 * 60 * 1000; // 2 minutes cache

      // Check if this is the first load (no lastLoadTime set)
      const isFirstLoad = lastLoadTime === 0;

      // Check if this is a page change or limit change by comparing with state
      const currentPage = state.projects.pagination?.page || 1;
      const currentLimit = state.projects.pagination?.limit || 10;
      const isPageChange = page !== currentPage;
      const isLimitChange = limit !== currentLimit;

      // If we have projects and they were loaded less than 2 minutes ago, use cached data
      // But always fetch fresh data on first load, page changes, limit changes, or when any filters are active
      if (
        !isFirstLoad &&
        !isPageChange &&
        !isLimitChange &&
        !search &&
        !customerName &&
        !articleNo &&
        !createdFrom &&
        !createdTo &&
        projects.length > 0 &&
        now - lastLoadTime < cacheTime
      ) {
        console.log('Using cached projects data from Redux store');
        return { success: true, data: projects, fromCache: true };
      }

      console.log(
        `Making fresh API call for ${isPageChange ? 'page change' : 'new data'}, page ${page}`
      );

      console.log('loadProjects action dispatched - fetching fresh data');

      try {
        // Build query parameters
        const queryParams = new URLSearchParams();
        queryParams.set('limit', limit.toString());
        queryParams.set('page', page.toString());

        if (search) queryParams.set('search', search);
        if (customerName) queryParams.set('customerName', customerName);
        if (articleNo) queryParams.set('articleNo', articleNo);
        if (createdFrom) queryParams.set('createdFrom', createdFrom);
        if (createdTo) queryParams.set('createdTo', createdTo);

        const response = await axiosInstance.get<{
          success: boolean;
          data: Project[];
          pagination?: Pagination;
        }>(`/v1/admin/project?${queryParams.toString()}`);
        console.log(`loadProjects response for page ${page}, limit ${limit}:`, response.data);

        // Always ensure we have pagination data
        const pagination = response.data.pagination || {
          total: response.data.data?.length || 0,
          pages: Math.ceil((response.data.data?.length || 0) / 10),
          page: 1,
          limit: 10,
        };

        // Make sure pages is calculated correctly
        if (!response.data.pagination?.pages) {
          pagination.pages = Math.max(1, Math.ceil(pagination.total / pagination.limit));
          console.log(
            `Calculated total pages: ${pagination.pages} from ${pagination.total} items and limit ${pagination.limit}`
          );
        }

        const responseWithPagination = {
          ...response.data,
          pagination: pagination,
          lastLoadTime: now,
        };

        return responseWithPagination;
      } catch (axiosError: any) {
        // More specific error handling for network issues
        if (!axiosError.response) {
          console.error('Network error - server may be down:', axiosError.message);
          throw new Error('Server is unavailable. Please check your connection.');
        }

        // Handle specific HTTP status codes
        if (axiosError.response.status === 401) {
          throw new Error('Authentication error. Please log in again.');
        } else if (axiosError.response.status === 403) {
          throw new Error('You do not have permission to access this resource.');
        } else if (axiosError.response.status >= 500) {
          throw new Error('Server error. Please try again later.');
        }

        // Re-throw the original error if it's not one of the above cases
        throw axiosError;
      }
    } catch (error: any) {
      console.error('loadProjects error:', error);

      // Provide a more useful error message
      const errorMessage = error.message || 'An error occurred';
      return rejectWithValue(errorMessage);
    }
  }
);

// New action to fetch projects by status
export const fetchProjectsByStatus = createAsyncThunk(
  'projects/fetchProjectsByStatus',
  async (
    params: {
      status: string;
      page?: number;
      limit?: number;
      search?: string;
      customerName?: string;
      articleNo?: string;
      createdFrom?: string;
      createdTo?: string;
    },
    { rejectWithValue, dispatch }
  ) => {
    try {
      // Clear any cache first to ensure we get fresh data
      dispatch(clearCache());

      const {
        status,
        page = 1,
        limit = 10,
        search = '',
        customerName = '',
        articleNo = '',
        createdFrom = '',
        createdTo = '',
      } = typeof params === 'string'
        ? {
            status: params,
            page: 1,
            limit: 10,
            search: '',
            customerName: '',
            articleNo: '',
            createdFrom: '',
            createdTo: '',
          } // For backward compatibility
        : params;

      console.log(
        `Fetching projects with status: ${status}, page: ${page}, limit: ${limit}, search: ${search}, customerName: ${customerName}, articleNo: ${articleNo}`
      );
      // Normalize status for API query
      const apiStatus = status.replace(' ', '_').toUpperCase();

      // Build query parameters
      const queryParams = new URLSearchParams();
      queryParams.set('status', apiStatus);
      queryParams.set('page', page.toString());
      queryParams.set('limit', limit.toString());

      if (search) queryParams.set('search', search);
      if (customerName) queryParams.set('customerName', customerName);
      if (articleNo) queryParams.set('articleNo', articleNo);
      if (createdFrom) queryParams.set('createdFrom', createdFrom);
      if (createdTo) queryParams.set('createdTo', createdTo);

      const apiUrl = `/v1/admin/project?${queryParams.toString()}`;
      console.log(`Making API call to: ${apiUrl}`);

      const response = await axiosInstance.get<{
        success: boolean;
        data: Project[];
        pagination?: Pagination;
      }>(apiUrl);

      console.log(`fetchProjectsByStatus response for page ${page}:`, {
        status: response.status,
        dataCount: response.data.data?.length,
        pagination: response.data.pagination,
      });

      // Ensure we have proper pagination data
      const pagination = response.data.pagination || {
        total: response.data.data?.length || 0,
        pages: Math.ceil((response.data.data?.length || 0) / 10),
        page: 1,
        limit: 10,
      };

      // Make sure pages is calculated correctly if it's not provided by API
      if (!response.data.pagination?.pages) {
        pagination.pages = Math.max(1, Math.ceil(pagination.total / pagination.limit));
        console.log(
          `Calculated total pages: ${pagination.pages} from ${pagination.total} items and limit ${pagination.limit}`
        );
      }

      return {
        data: response.data.data,
        status,
        pagination,
      };
    } catch (error: any) {
      console.error('fetchProjectsByStatus error:', error);
      return rejectWithValue(
        error.response?.data || 'An error occurred while fetching projects by status'
      );
    }
  }
);

// New action to search projects
export const searchProjects = createAsyncThunk(
  'projects/searchProjects',
  async (
    params: {
      search: string;
      page?: number;
      limit?: number;
      customerName?: string;
      articleNo?: string;
      createdFrom?: string;
      createdTo?: string;
    },
    { rejectWithValue, dispatch }
  ) => {
    try {
      // Clear any cache first to ensure we get fresh data
      dispatch(clearCache());

      const {
        search,
        page = 1,
        limit = 10,
        customerName = '',
        articleNo = '',
        createdFrom = '',
        createdTo = '',
      } = params;

      console.log(`Searching projects with query: ${search}, page: ${page}, limit: ${limit}`);

      // Build query parameters
      const queryParams = new URLSearchParams();
      queryParams.set('search', search);
      queryParams.set('page', page.toString());
      queryParams.set('limit', limit.toString());

      if (customerName) queryParams.set('customerName', customerName);
      if (articleNo) queryParams.set('articleNo', articleNo);
      if (createdFrom) queryParams.set('createdFrom', createdFrom);
      if (createdTo) queryParams.set('createdTo', createdTo);

      const apiUrl = `/v1/admin/project?${queryParams.toString()}`;
      console.log(`Making API call to: ${apiUrl}`);

      const response = await axiosInstance.get<{
        success: boolean;
        data: Project[];
        pagination?: Pagination;
      }>(apiUrl);

      console.log(`searchProjects response for page ${page}:`, {
        status: response.status,
        dataCount: response.data.data?.length,
        pagination: response.data.pagination,
      });

      // Ensure we have proper pagination data
      const pagination = response.data.pagination || {
        total: response.data.data?.length || 0,
        pages: Math.ceil((response.data.data?.length || 0) / 10),
        page: 1,
        limit: 10,
      };

      // Make sure pages is calculated correctly if it's not provided by API
      if (!response.data.pagination?.pages) {
        pagination.pages = Math.max(1, Math.ceil(pagination.total / pagination.limit));
        console.log(
          `Calculated total pages: ${pagination.pages} from ${pagination.total} items and limit ${pagination.limit}`
        );
      }

      return {
        data: response.data.data,
        search,
        pagination,
      };
    } catch (error: any) {
      console.error('searchProjects error:', error);
      return rejectWithValue(error.response?.data || 'An error occurred while searching projects');
    }
  }
);

// Fetch project count for dashboard
export const fetchProjectCount = createAsyncThunk(
  'projects/fetchProjectCount',
  async (_, { rejectWithValue }) => {
    try {
      console.log('Fetching project count for dashboard');
      const endpoint = '/v1/admin/project/count';

      const response = await axiosInstance.get<{
        success: boolean;
        data: {
          count: number;
          lastProject?: { name: string };
          totalAmount: number;
          paymentReceived: number;
          billedAmount: number;
          pipelineAmount: number;
          statusCounts: Record<string, number>;
        };
      }>(endpoint);

      console.log('fetchProjectCount response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('fetchProjectCount error:', error);
      return rejectWithValue(error.response?.data || 'Failed to fetch project count');
    }
  }
);

// Create a new project
export const createProject = createAsyncThunk(
  'projects/createProject',
  async (projectData: any, { rejectWithValue }) => {
    try {
      console.log(
        'Creating new project with data:',
        JSON.stringify(
          {
            ...projectData,
            projectImage: projectData.projectImage
              ? `[File: ${projectData.projectImage.name}, type: ${projectData.projectImage.type}, size: ${projectData.projectImage.size} bytes]`
              : null,
          },
          null,
          2
        )
      );

      // Create a copy of the project data for manipulation
      const projectDataToSend = { ...projectData };

      // Check if there's an image file in the data
      if (projectData.projectImage instanceof File) {
        try {
          console.log('Uploading image file to S3:', projectData.projectImage.name);

          // Upload the image directly to S3 using pre-signed URL approach
          const imageUrl = await uploadFileToS3(projectData.projectImage);

          console.log('Image uploaded successfully, received URL:', imageUrl);

          // Replace the file object with the URL string
          projectDataToSend.profilePicture = imageUrl;
        } catch (uploadError) {
          console.error('Error uploading image to S3:', uploadError);
          return rejectWithValue('Failed to upload image. Please try again.');
        }
      } else {
        console.log('No image file provided for the project');
        // Don't set a default profile picture - leave it null/undefined
        // This allows the frontend to properly handle "no image" state
        if (!projectDataToSend.profilePicture) {
          projectDataToSend.profilePicture = null;
          console.log('No profile picture provided - setting to null');
        }
      }

      // Remove the file object as we don't need to send it to the backend
      delete projectDataToSend.projectImage;

      console.log('Sending project data to backend:', JSON.stringify(projectDataToSend, null, 2));

      // Send the project data with the image URL to the backend
      const response = await axiosInstance.post('/v1/admin/project', projectDataToSend);
      console.log('createProject response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('createProject error:', error);
      console.error('Error details:', error.response?.data);
      return rejectWithValue(
        error.response?.data?.error?.message ||
          error.response?.data?.message ||
          'Failed to create project'
      );
    }
  }
);

// Update an existing project
export const updateProject = createAsyncThunk(
  'projects/updateProject',
  async (
    { projectId, projectData }: { projectId: string; projectData: any },
    { rejectWithValue, dispatch }
  ) => {
    try {
      console.log(`Updating project ${projectId} with data:`, projectData);

      // Create a copy of the project data for manipulation
      const projectDataToSend = { ...projectData };

      // Check if there's an image file in the data
      if (projectData.projectImage instanceof File) {
        try {
          // Upload the image directly to S3 using pre-signed URL approach
          const imageUrl = await uploadFileToS3(projectData.projectImage);

          // Replace the file object with the URL string
          projectDataToSend.profilePicture = imageUrl;
        } catch (uploadError) {
          console.error('Error uploading image to S3:', uploadError);
          return rejectWithValue('Failed to upload image. Please try again.');
        }
      }

      // Remove the file object as we don't need to send it to the backend
      delete projectDataToSend.projectImage;

      // Send the project data with the image URL to the backend
      const response = await axiosInstance.put(
        `/v1/admin/project/update/${projectId}`,
        projectDataToSend
      );

      console.log('updateProject response:', response.data);

      // Force reload projects data without using cache
      // This ensures the UI is always up-to-date after an update
      dispatch(clearCache()); // Use the proper action creator

      // Load fresh project data after the update is complete
      dispatch(loadProjects({ page: 1, limit: 10 }));

      return response.data;
    } catch (error: any) {
      console.error('updateProject error:', error);
      return rejectWithValue(
        error.response?.data?.error?.message ||
          error.response?.data?.message ||
          'Failed to update project'
      );
    }
  }
);

// Fetch dashboard metrics for all projects
export const fetchDashboardMetrics = createAsyncThunk(
  'projects/fetchDashboardMetrics',
  async (
    dateRange: { startDate: string; endDate: string; forceRefresh?: boolean },
    { rejectWithValue, getState }
  ) => {
    try {
      console.log(
        `[Action] fetchDashboardMetrics - Fetching metrics for date range: ${dateRange.startDate} to ${dateRange.endDate}, forceRefresh: ${dateRange.forceRefresh || false}`
      );

      // Create a cache key based on the date range
      const cacheKey = `${dateRange.startDate}_${dateRange.endDate}`;

      // Get current state to check cache
      const state = getState() as any;
      const { dashboardMetricsCache, dashboardMetrics } = state.projects;
      const user = state.auth?.user;

      console.log(
        `[Action] fetchDashboardMetrics - Auth user:`,
        user
          ? {
              id: user.id,
              role: user.role,
              designation: user.designation,
              isAdmin:
                user.role === 'ADMIN' ||
                user.role === 'superAdmin' ||
                user.designation === 'superAdmin',
            }
          : 'No user'
      );

      // Check if we have valid cached data (less than 2 minutes old and for the same date range)
      const cacheValidityPeriod = 2 * 60 * 1000; // 2 minutes in milliseconds
      const now_ts = Date.now();

      if (
        !dateRange.forceRefresh &&
        dashboardMetrics &&
        dashboardMetricsCache?.cacheKey === cacheKey &&
        now_ts - dashboardMetricsCache?.timestamp < cacheValidityPeriod
      ) {
        console.log(
          '[Action] fetchDashboardMetrics - Using cached metrics for date range:',
          dateRange.startDate,
          'to',
          dateRange.endDate
        );
        return dashboardMetrics;
      }

      console.log(
        `[Action] fetchDashboardMetrics - NETWORK REQUEST - Making API call to /v1/admin/project/dashboard/all${dateRange.forceRefresh ? ' (forced refresh)' : ''}`
      );

      // Check auth token
      const authToken = document.cookie
        .split('; ')
        .find(row => row.startsWith('pms_admin_=') || row.startsWith('pms_employee_='))
        ?.split('=')[1];

      console.log(`[Action] fetchDashboardMetrics - Auth token present: ${Boolean(authToken)}`);

      // Construct API URL with query parameters
      const apiUrl = `/v1/admin/project/dashboard/all?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}`;

      const response = await axiosInstance.get<{
        success: boolean;
        data: {
          totalProjectsCount: number;
          projectsInRangeCount: number;
          financialMetrics: {
            paymentReceivedSum: number;
            billedSum: number;
            dueSum: number;
            pipelineSum: number;
          };
          dailyPayments: Array<{ date: string; amount: number }>;
          statusCounts: Record<string, number>; // Added statusCounts to type
        };
      }>(apiUrl);

      console.log('[Action] fetchDashboardMetrics - Response:', response.data);

      if (response.data.success) {
        // Ensure all required properties exist to prevent runtime errors
        const responseData = {
          ...response.data.data,
          // Initialize statusCounts if missing
          statusCounts: response.data.data.statusCounts || {},
          timestamp: now_ts,
          cacheKey,
        };

        console.log('[Action] fetchDashboardMetrics - Processed response data:', responseData);
        return responseData;
      } else {
        return rejectWithValue('Failed to fetch dashboard metrics');
      }
    } catch (error: any) {
      console.error('Error fetching dashboard metrics:', error);
      return rejectWithValue(
        error.response?.data?.error?.message || 'Failed to fetch dashboard metrics'
      );
    }
  }
);

// Fetch origin project by ID for reopen view
export const fetchOriginProject = createAsyncThunk(
  'projects/fetchOriginProject',
  async (projectId: number, { rejectWithValue }) => {
    try {
      console.log(`[Action] fetchOriginProject - Fetching project ${projectId}`);

      const response = await axiosInstance.get<{
        success: boolean;
        data: Project;
      }>(`/v1/admin/project/${projectId}`);

      console.log('[Action] fetchOriginProject - Response:', response.data);

      if (response.data.success && response.data.data) {
        return response.data.data;
      } else {
        return rejectWithValue('Failed to fetch origin project');
      }
    } catch (error: any) {
      console.error('Error fetching origin project:', error);
      return rejectWithValue(
        error.response?.data?.error?.message || 'Failed to fetch origin project'
      );
    }
  }
);

export const fetchReopenedProject = createAsyncThunk(
  'projects/fetchReopenedProject',
  async (projectId: number, { rejectWithValue }) => {
    try {
      console.log(`[Action] fetchReopenedProject - Fetching project ${projectId}`);

      const response = await axiosInstance.get<{
        success: boolean;
        data: Project;
      }>(`/v1/admin/project/${projectId}`);

      console.log('[Action] fetchReopenedProject - Response:', response.data);

      if (response.data.success && response.data.data) {
        return response.data.data;
      } else {
        return rejectWithValue('Failed to fetch reopened project');
      }
    } catch (error: any) {
      console.error('Error fetching reopened project:', error);
      return rejectWithValue(
        error.response?.data?.error?.message || 'Failed to fetch reopened project'
      );
    }
  }
);
